import { isIpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import {
  API_RESOURCE_CONFLICT,
  type Policy,
  type PolicyScopeType,
  type PolicyWithApiScopes,
  type PolicyWithApiScopesGetResponseBody,
  type PolicyWithApiScopesPatchRequestBody,
  type PolicyWithApiScopesPatchResponseBody,
  type PolicyWithApiScopesPutRequestBody,
  type PolicyWithApiScopesPutResponseBody,
  type PolicyWithAttributeScopes,
  type PolicyWithAttributeScopesGetResponseBody,
  type PolicyWithAttributeScopesPatchRequestBody,
  type PolicyWithAttributeScopesPatchResponseBody,
  type PolicyWithAttributeScopesPutRequestBody,
  type PolicyWithAttributeScopesPutResponseBody,
  isPolicyWithApiScopes,
  isPolicyWithAttributeScopes,
  safeAbsurd,
} from '@ipfabric/utils';
import { Button } from 'components/Button/Button';
import { Switch } from 'components/Switch/Switch';
import { useLocale } from 'containers/App/LocaleContext';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import { useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useApi } from 'utils/request/useApi';
import { ApiPolicyForm } from './ApiPolicyForm';
import { AttributePolicyForm } from './AttributePolicyForm';
import { handlePolicyResourceConflict } from './handlePolicyResourceConflict';
import {
  type ApiPolicyFormValues,
  emptyApiPolicyFormikValues,
  useApiPolicyFormik,
} from './useApiPolicyFormik';
import {
  type AttributePolicyFormValues,
  emptyAttributePolicyFormikValues,
  useAttributePolicyFormik,
} from './useAttributePolicyFormik';

interface Props {
  policyToCopy: Policy | null;
  policyToEdit: Policy | null;
  onCancel: () => void;
}

export const EditPolicy: FC<Props> = (props) => {
  const api = useApi();
  const { showNotification } = useNotifications();
  const [policy, setPolicy] = useState<
    PolicyWithApiScopes | PolicyWithAttributeScopes | null
  >(null);
  const [loading, setLoading] = useLoadingState();
  const [saving, setSaving] = useLoadingState();
  const {
    administrationPolicies: {
      form: policiesFormMsg,
      request: requestMsg,
      scopeType: scopeTypeMsgs,
    },
  } = useLocale();
  const scopeType =
    props.policyToEdit?.scopeType ?? props.policyToCopy?.scopeType;

  // biome-ignore lint/correctness/useExhaustiveDependencies(props.policyToCopy): used just for naming of the result
  const initialApiValues = useMemo(
    () =>
      policy && isPolicyWithApiScopes(policy)
        ? {
            name:
              props.policyToCopy === null
                ? policy.name
                : `${policy.name} (copy)`,
            description: policy.description,
            apiScopeIds: policy.apiScopeIds,
          }
        : emptyApiPolicyFormikValues,
    [policy],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies(props.policyToCopy): used just for naming of the result
  const initialAttributeValues = useMemo(
    () =>
      policy && isPolicyWithAttributeScopes(policy)
        ? {
            name:
              props.policyToCopy === null
                ? policy.name
                : `${policy.name} (copy)`,
            description: policy.description,
            attributeFilters: policy.attributeFilters,
          }
        : emptyAttributePolicyFormikValues,
    [policy],
  );

  const handleApiSubmit = async (values: ApiPolicyFormValues) => {
    if (props.policyToCopy === null && props.policyToEdit === null) {
      return;
    }
    try {
      await withLoadingIndicator(setSaving)(async () =>
        props.policyToCopy !== null
          ? api.request<
              PolicyWithApiScopesPutResponseBody,
              PolicyWithApiScopesPutRequestBody
            >({
              method: 'POST',
              url: 'policies/scopes/api',
              data: values,
            })
          : api.request<
              PolicyWithApiScopesPatchResponseBody,
              PolicyWithApiScopesPatchRequestBody
            >({
              method: 'PATCH',
              url: `policies/scopes/api/${props.policyToEdit?.id}`,
              data: values,
            }),
      );
      props.onCancel();
    } catch (e) {
      if (isIpfError(e, 'API') && e.code === API_RESOURCE_CONFLICT) {
        handlePolicyResourceConflict(
          showNotification,
          requestMsg.resourceConflict,
          apiFormik,
        );
      } else {
        throw e;
      }
    }
  };

  const handleAttributeSubmit = async (values: AttributePolicyFormValues) => {
    if (props.policyToCopy === null && props.policyToEdit === null) {
      return;
    }
    try {
      await withLoadingIndicator(setSaving)(async () =>
        props.policyToCopy !== null
          ? api.request<
              PolicyWithAttributeScopesPutResponseBody,
              PolicyWithAttributeScopesPutRequestBody
            >({
              method: 'POST',
              url: 'policies/scopes/attributes',
              data: values,
            })
          : api.request<
              PolicyWithAttributeScopesPatchResponseBody,
              PolicyWithAttributeScopesPatchRequestBody
            >({
              method: 'PATCH',
              url: `policies/scopes/attributes/${props.policyToEdit?.id}`,
              data: values,
            }),
      );
      props.onCancel();
    } catch (e) {
      if (isIpfError(e, 'API') && e.code === API_RESOURCE_CONFLICT) {
        handlePolicyResourceConflict(
          showNotification,
          requestMsg.resourceConflict,
          attributeFormik,
        );
      } else {
        throw e;
      }
    }
  };

  const apiFormik = useApiPolicyFormik({
    initialValues: initialApiValues,
    onSubmit: handleApiSubmit,
  });

  const attributeFormik = useAttributePolicyFormik({
    initialValues: initialAttributeValues,
    onSubmit: handleAttributeSubmit,
  });

  const handleSubmit = () => {
    if (scopeType === 'apiScopes') {
      apiFormik.submitForm();
    } else if (scopeType === 'attributeScopes') {
      attributeFormik.submitForm();
    } else if (scopeType === undefined) {
      stumble('undefined scopeType');
    } else {
      safeAbsurd(scopeType, 'unknown scopeType');
    }
  };

  useEffect(() => {
    (async () => {
      if (!scopeType) {
        return;
      }

      const url = `/policies/scopes/${scopeType === 'apiScopes' ? 'api' : 'attributes'}`;

      const loadedPolicy = await withLoadingIndicator(setLoading)(async () =>
        api.request<
          | PolicyWithApiScopesGetResponseBody
          | PolicyWithAttributeScopesGetResponseBody
        >({
          method: 'GET',
          url: `${url}/${props.policyToEdit?.id ?? props.policyToCopy?.id}`,
        }),
      );

      setPolicy(loadedPolicy);
    })();
  }, [props.policyToEdit, props.policyToCopy, scopeType]);

  if (loading === 'LOADING') {
    return <LoaderAndText />;
  }

  // system policy is editable only when it's copied -> saved as new
  const disabled = policy?.isSystem === true && props.policyToCopy === null;

  return (
    <>
      <div className='w-80'>
        <div className='form__label'>{policiesFormMsg.scopeType}</div>
        <Switch<PolicyScopeType>
          options={[
            { label: scopeTypeMsgs.attributeScopes, value: 'attributeScopes' },
            { label: scopeTypeMsgs.apiScopes, value: 'apiScopes' },
          ]}
          value={scopeType ?? 'apiScopes'}
          onChange={() => {}}
          disabled
        />
      </div>
      {scopeType === 'apiScopes' && (
        <ApiPolicyForm
          formik={apiFormik}
          // copied 'all-tables' policy can be edited
          disabled={disabled}
        />
      )}
      {scopeType === 'attributeScopes' && (
        <AttributePolicyForm formik={attributeFormik} />
      )}
      <div className='mt-8'>
        <Button
          type='button'
          className='mr-2'
          variant='secondary'
          onClick={() => {
            props.onCancel();
          }}
        >
          {policiesFormMsg.buttons.cancel}
        </Button>
        <Button
          type='button'
          variant='primary'
          onClick={handleSubmit}
          loading={saving === 'LOADING'}
          disabled={disabled}
        >
          {policiesFormMsg.buttons.save}
        </Button>
      </div>
    </>
  );
};
